import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSessionNameEditingContext } from './sessionNameEditingContext';

export type SessionNameEditorHandle = {
  startEditing: () => void;
};

type SessionNameEditorProps = {
  name: string;
  customName?: boolean;
  onRename?: (name: string) => void;
  /** Overrides the display element's data-testid (default: 'session-name'). */
  testId?: string;
  /** Overrides the edit input's data-testid (default: 'session-name-input'). */
  inputTestId?: string;
};

export const SessionNameEditor = forwardRef<SessionNameEditorHandle, SessionNameEditorProps>(
  function SessionNameEditor(
    { name, customName, onRename, testId = 'session-name', inputTestId = 'session-name-input' },
    ref
  ) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(name || '');
    const inputRef = useRef<HTMLInputElement>(null);
    const editingContext = useSessionNameEditingContext();

    useEffect(() => {
      if (!isEditing) {
        setEditValue(name || '');
      }
    }, [isEditing, name]);

    useEffect(() => {
      if (!isEditing || !inputRef.current) return;
      inputRef.current.focus();
      inputRef.current.select();
    }, [isEditing]);

    const stopEditing = () => {
      setIsEditing(false);
      editingContext?.setIsEditing(false);
    };

    const startEditing = useCallback(() => {
      if (!onRename) return;
      setIsEditing(true);
      editingContext?.setIsEditing(true);
    }, [editingContext, onRename]);

    const save = () => {
      const trimmed = editValue.trim();
      if (trimmed && onRename) {
        onRename(trimmed);
      }
      stopEditing();
    };

    useImperativeHandle(ref, () => ({ startEditing }), [startEditing]);

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        save();
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        setEditValue(name || '');
        stopEditing();
      }
    };

    const handleInputBlur = () => {
      if (editValue.trim() !== (name || '')) {
        save();
        return;
      }
      stopEditing();
    };

    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onBlur={handleInputBlur}
          onClick={(e) => e.stopPropagation()}
          className="bg-transparent border-b border-primary outline-none w-full text-sm"
          data-testid={inputTestId}
        />
      );
    }

    return (
      <div className="min-w-0 flex items-center gap-1">
        <h4
          className="min-w-0 flex-1 font-medium truncate"
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            startEditing();
          }}
          data-testid={testId}
          title={name || 'Untitled Session'}
        >
          {name || 'Untitled Session'}
        </h4>
        {customName && (
          <span
            className="text-muted-foreground opacity-60 shrink-0"
            data-testid="session-custom-indicator"
            title="Custom name"
          >
            *
          </span>
        )}
      </div>
    );
  }
);
